import type { WorkflowRequest, WorkflowRequestDraftApprovalStep } from '../model';
import { normalizeWorkflowApprovalSteps } from '../workflowApprovalStages';
import { canonicalWorkflowJSON, normalizeWorkflowReference } from './workflowValueNormalization';

export type WorkflowSubmitIntent = {
  draftId: string;
  draftVersion: number;
  idempotencyKey: string;
};

export type DraftSaveStatus = 'idle' | 'pending' | 'saving' | 'saved' | 'error';
export type DraftSaveConflict = 'template-version' | 'draft-version';

export type WorkflowDraftSnapshot = {
  templateId: string;
  templateVersionId: string;
  title: string;
  values: Record<string, unknown>;
  approvalSteps: WorkflowRequestDraftApprovalStep[];
  fingerprint: string;
  idempotencyKey: string;
};

export type PersistedWorkflowDraft = {
  id: string;
  draftVersion: number;
  approvalSteps?: WorkflowRequestDraftApprovalStep[];
};

export type PendingWorkflowSubmitIntent = WorkflowSubmitIntent & {
  fingerprint: string;
};

export function cloneRequestApprovalSteps(steps: WorkflowRequestDraftApprovalStep[]) { return normalizeWorkflowApprovalSteps(steps.map((step) => ({ ...step, resolverConfig: { ...(step.resolverConfig ?? {}) } }))); }

export function workflowRequestComposerFingerprint(
  templateId: string,
  title: string,
  values: Readonly<Record<string, unknown>>,
  approvalSteps: readonly WorkflowRequestDraftApprovalStep[],
): string {
  return JSON.stringify({ templateId, title, values, approvalSteps }, (_key, item: unknown) => {
    if (!item || typeof item !== 'object' || Array.isArray(item)) return item;
    return Object.fromEntries(Object.entries(item as Record<string, unknown>).sort(([left], [right]) => left.localeCompare(right)));
  });
}
export function workflowRequestDraftApprovalStepSnapshot(step: WorkflowRequestDraftApprovalStep) {
  return {
    sourceTemplateStepId: normalizeWorkflowReference(step.sourceTemplateStepId),
    order: step.order,
    stageOrder: step.stageOrder,
    label: step.label.trim(),
    approvalLabelId: normalizeWorkflowReference(step.approvalLabelId),
    resolverType: step.resolverType.trim().toUpperCase(),
    resolverConfig: canonicalWorkflowJSON(step.resolverConfig ?? {}),
    missingPolicy: step.missingPolicy?.trim().toUpperCase() || 'FAIL',
    selfPolicy: step.selfPolicy?.trim().toUpperCase() || 'SKIP',
    duplicatePolicy: step.duplicatePolicy?.trim().toUpperCase() || 'SKIP',
    dueAfterMinutes: step.dueAfterMinutes ?? null,
    requesterEditable: step.requesterEditable,
  };
}
export function workflowDraftContentSnapshot(
  templateId: string,
  templateVersionId: string,
  title: string,
  values: Readonly<Record<string, unknown>>,
  approvalSteps: readonly WorkflowRequestDraftApprovalStep[],
) {
  return canonicalWorkflowJSON({
    templateId: normalizeWorkflowReference(templateId),
    templateVersionId: normalizeWorkflowReference(templateVersionId),
    title: title.trim(),
    values,
    approvalSteps: [...approvalSteps]
      .sort((left, right) => left.order - right.order || left.label.localeCompare(right.label))
      .map(workflowRequestDraftApprovalStepSnapshot),
  });
}
export function workflowDraftResponseMatchesSnapshot(request: WorkflowRequest, snapshot: WorkflowDraftSnapshot): boolean {
  return JSON.stringify(workflowDraftContentSnapshot(
    request.templateId,
    request.templateVersionId,
    request.title,
    request.values ?? {},
    request.draftApprovalSteps ?? [],
  )) === JSON.stringify(workflowDraftContentSnapshot(
    snapshot.templateId,
    snapshot.templateVersionId,
    snapshot.title,
    snapshot.values,
    snapshot.approvalSteps,
  ));
}
